<template>
  <div class="col-12">
    <div class="row d-flex justify-content-center align-items-center">
      <div class="col-10 col-lg-8">
        <div class="row text-center ml-5">
          <div class="mb-5">
            <img src="../../assets/tn_cybertech_grad_logo.png" class="tn-logo" style="width: 200px; height: auto;" alt="">
          </div>
          <h3 class="ui inverted purple text"><strong>My Account Applications</strong></h3>
          <p class="text-muted fw-light mt-4">
            View the status of your account applications. If your application was rejected, you can update your documents.
          </p>
        </div>
        <div class="ui fitted divider mt-2"></div>
      </div>
    </div>

    <div class="row d-flex justify-content-center align-items-center mt-5">
      <div class="col-11 col-lg-10">
        <div v-bind:class="{ 'ui form loading': isBusy() }">

          <!-- Empty state -->
          <div v-if="!isBusy() && accounts.length === 0" class="ui placeholder segment">
            <div class="ui icon header">
              <i class="file outline icon"></i>
              No Applications Found
            </div>
            <p class="text-center text-muted">You haven't submitted any account applications yet.</p>
            <button class="ui purple button" @click="openAccount()">Open an Account</button>
          </div>

          <!-- Applications list -->
          <div v-if="accounts.length > 0" class="row">
            <div class="col-12 col-md-6 col-lg-4 mb-4" v-for="account in accounts" :key="account.id">
              <div class="card card-shadow h-100" :class="getStatusBorderClass(account.status)">
                <div class="card-body d-flex flex-column">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h5 class="card-title mb-0"><strong>{{ account.accountType }}</strong></h5>
                    <span class="ui label" :class="getStatusLabelClass(account.status)">
                      {{ getStatusText(account.status) }}
                    </span>
                  </div>

                  <p class="card-text text-muted mb-2">
                    <i class="money icon"></i> Currency: <strong>{{ account.currency }}</strong>
                  </p>

                  <p v-if="account.createdAt" class="card-text text-muted mb-2">
                    <i class="calendar icon"></i> Applied: {{ formatDate(account.createdAt) }}
                  </p>

                  <!-- Rejection reason -->
                  <div v-if="account.status === 'PENDING_REJECTED' && account.rejectionReason"
                       class="ui orange message mt-2 mb-3">
                    <div class="header">Rejection Reason</div>
                    <p>{{ account.rejectionReason }}</p>
                  </div>

                  <!-- Action buttons -->
                  <div class="mt-auto pt-3">
                    <button v-if="account.status === 'PENDING_REJECTED'"
                            class="ui purple basic button fluid"
                            @click="updateDocuments(account)">
                      <i class="upload icon"></i> Update Documents
                    </button>
                    <button v-else-if="account.status === 'UNDER_REVIEW'"
                            class="ui grey basic button fluid" disabled>
                      <i class="clock icon"></i> Under Review
                    </button>
                    <button v-else
                            class="ui grey basic button fluid" disabled>
                      <i class="hourglass icon"></i> Pending Authorization
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="mt-5">
          <button @click="back()" class="ui grey large basic button fluid">Back</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import baseMethods from "@/utils/baseMethods";
import { useAuthStore } from '@/stores/auth';
import { useAccountOpeningStore } from '@/stores/accountOpening';

export default {
  name: "ViewApplications",
  setup() {
    const authStore = useAuthStore();
    const accountOpeningStore = useAccountOpeningStore();
    return { authStore, accountOpeningStore };
  },
  data() {
    return {
      accounts: [],
      errors: {},
      operations: 0,
    };
  },
  computed: {
    userId() { return this.authStore.userId; },
    phone() { return this.authStore.phone; },
  },
  mounted() {
    this.getAccountApplications();
  },
  methods: {
    getAccountApplications() {
      const phone = this.phone || this.authStore.user?.phone;
      if (!phone) {
        this.$router.push('/omni');
        return;
      }

      this.getNoAuthAndNoBaseUrl(`/account-opening/accounts/applications/${phone}`)
        .then(res => {
          if (res && res.success && res.body) {
            this.accounts = res.body;
          }
        });
    },

    getStatusText(status) {
      const statusMap = {
        'PENDING_MANUAL': 'Pending',
        'PENDING_REJECTED': 'Rejected',
        'PENDING_UPGRADE': 'Pending',
        'UNDER_REVIEW': 'Under Review',
        'APPROVED': 'Approved',
        'COMPLETED': 'Completed',
      };
      return statusMap[status] || status;
    },

    getStatusLabelClass(status) {
      const classMap = {
        'PENDING_MANUAL': 'yellow',
        'PENDING_REJECTED': 'red',
        'PENDING_UPGRADE': 'yellow',
        'UNDER_REVIEW': 'blue',
        'APPROVED': 'green',
        'COMPLETED': 'green',
      };
      return classMap[status] || 'grey';
    },

    getStatusBorderClass(status) {
      const classMap = {
        'PENDING_MANUAL': 'border-warning',
        'PENDING_REJECTED': 'border-danger',
        'PENDING_UPGRADE': 'border-warning',
        'UNDER_REVIEW': 'border-primary',
        'APPROVED': 'border-success',
        'COMPLETED': 'border-success',
      };
      return classMap[status] || 'border-dark';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    updateDocuments(account) {
      // Store account info for update flow
      this.accountOpeningStore.setAccountType(account.accountType);
      this.accountOpeningStore.setCurrencyType(account.currency);
      this.$router.push('/omni/upload-documents');
    },

    openAccount() {
      this.$router.push('/omni/terms-and-conditions');
    },

    back() {
      this.$router.push('/omni/home');
    },

    ...baseMethods,
  }
};
</script>

<style scoped>
.border-warning {
  border-left: 4px solid #f0ad4e !important;
}
.border-danger {
  border-left: 4px solid #d9534f !important;
}
.border-primary {
  border-left: 4px solid #337ab7 !important;
}
.border-success {
  border-left: 4px solid #5cb85c !important;
}
</style>
